import { AnalysisCidList } from '@/features/analyses/pme/components/analysis-cid-list.component';
import { useCidDescriptions } from '@/features/analyses/pme/use-cid-descriptions.hook';
import {
  AnalysisTabEmptyState,
  AnalysisTabSkeleton,
} from '@/features/analyses/components';
import { Tag, Text } from '@noorden-insurtech/alba-design-system';
import type {
  PmeAnalysisAfastadoRow,
  PmeAnalysisAgeCapitalRow,
  PmeAnalysisCoverageRow,
  PmeAnalysisCriterionRow,
  PmeAnalysisServiceRow,
} from '../utils/derive-analysis-tabs';

// ─── Aba "Critérios de Aceitação" ───────────────────────────────────────────

/**
 * Status de uma sub-regra: cada linha responde por si. Linha sem status
 * próprio (fallback) herda o do critério.
 */
function lineStatusAt(criterion: PmeAnalysisCriterionRow, index: number) {
  return criterion.lineStatus?.[index] ?? criterion.status;
}

/** Laranja quando a sub-regra é motivo de análise; cinza quando está OK. */
function lineColor(
  criterion: PmeAnalysisCriterionRow,
  index: number,
  criterionColor: string
): string {
  return lineStatusAt(criterion, index) === 'Análise'
    ? criterionColor
    : 'text-alba-gray-600';
}

function CriterionStatusTag({ status }: { status: 'OK' | 'Análise' }) {
  return (
    <Tag.Root colorScheme={status === 'OK' ? 'success' : 'yellow'} size='sm'>
      <Tag.Label>{status}</Tag.Label>
    </Tag.Root>
  );
}

export function PmeAcceptanceCriteriaTab({
  criteria,
}: {
  criteria: PmeAnalysisCriterionRow[];
}) {
  if (!criteria.length) {
    return (
      <AnalysisTabEmptyState message='Nenhum critério de aceitação pendente para esta proposta.' />
    );
  }

  return (
    <div className='flex flex-col overflow-x-auto'>
      <div className='min-w-[720px]'>
        <div className='grid grid-cols-[1.3fr_3fr_0.9fr_0.6fr] gap-4 pb-4'>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Critérios
          </Text>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Regras
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Resultado
          </Text>
          <Text className='text-center font-montserrat text-xs font-bold text-alba-black-500'>
            Status
          </Text>
        </div>

        {criteria.map((criterion, index) => {
          const isUnderAnalysis = criterion.status === 'Análise';
          const labelColor = isUnderAnalysis
            ? 'text-[#E8833A]'
            : 'text-alba-black-500';
          const textColor = isUnderAnalysis
            ? 'text-[#E8833A]'
            : 'text-alba-gray-600';

          return (
            <div
              key={`${criterion.name}-${index}`}
              className='grid grid-cols-[1.3fr_3fr_0.9fr_0.6fr] items-center gap-4 py-3'
            >
              <Text
                className={`font-montserrat text-sm font-medium ${labelColor}`}
              >
                {criterion.name}
              </Text>
              <div className='flex flex-col gap-1'>
                {criterion.rules.map((rule, i) => (
                  <Text
                    key={i}
                    className={`font-montserrat text-xs whitespace-pre-line ${lineColor(criterion, i, textColor)}`}
                  >
                    {rule}
                  </Text>
                ))}
              </div>
              <div className='flex flex-col gap-1 text-right'>
                {criterion.results.map((result, i) => (
                  <Text
                    key={i}
                    className={`font-montserrat text-xs ${lineColor(criterion, i, textColor)}`}
                  >
                    {result}
                  </Text>
                ))}
              </div>
              <div className='flex flex-col items-center gap-1'>
                {criterion.rules.length === 0 ? (
                  <CriterionStatusTag status={criterion.status} />
                ) : (
                  criterion.rules.map((_, i) => (
                    <CriterionStatusTag
                      key={i}
                      status={lineStatusAt(criterion, i)}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Aba "Coberturas e Serviços" ────────────────────────────────────────────

export function PmeCoveragesServicesTab({
  coverages,
  services,
}: {
  coverages: PmeAnalysisCoverageRow[];
  services: PmeAnalysisServiceRow[];
}) {
  if (!coverages.length && !services.length) {
    return (
      <AnalysisTabEmptyState message='Nenhuma cobertura ou serviço contratado disponível para exibição.' />
    );
  }

  return (
    <div className='flex flex-col gap-8 overflow-x-auto'>
      <div className='min-w-[640px]'>
        <div className='grid grid-cols-[2fr_1.2fr_1fr_1fr] gap-4 pb-4'>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Coberturas
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Capital Segurado (I$) Total
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Taxa Média
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Prêmio Mensal
          </Text>
        </div>

        {coverages.map((coverage, index) => (
          <div
            key={`${coverage.name}-${index}`}
            className='grid grid-cols-[2fr_1.2fr_1fr_1fr] gap-4 py-3'
          >
            <div className='flex flex-col'>
              <Text className='font-montserrat text-sm text-alba-black-500'>
                {coverage.name}
              </Text>
              {coverage.detail ? (
                <Text className='font-montserrat text-xs text-alba-gray-600'>
                  {coverage.detail}
                </Text>
              ) : null}
            </div>
            <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
              {coverage.totalInsuredCapital}
            </Text>
            <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
              {coverage.averageRate}
            </Text>
            <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
              {coverage.monthlyPremium}
            </Text>
          </div>
        ))}
      </div>

      <div className='min-w-[640px]'>
        <div className='grid grid-cols-[3.2fr_1fr_1fr] gap-4 pb-4'>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Serviços e Assistências
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Valor por Segurado
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Valor Mensal
          </Text>
        </div>

        {services.map((service, index) => (
          <div
            key={`${service.name}-${index}`}
            className='grid grid-cols-[3.2fr_1fr_1fr] gap-4 py-3'
          >
            <Text className='font-montserrat text-sm text-alba-black-500'>
              {service.name}
            </Text>
            <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
              {service.valuePerInsured}
            </Text>
            <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
              {service.monthlyValue}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Aba "Idade / Capital" ──────────────────────────────────────────────────

export function PmeAgeCapitalTab({
  rows,
  isLoading = false,
}: {
  rows: PmeAnalysisAgeCapitalRow[];
  isLoading?: boolean;
}) {
  if (isLoading) {
    return (
      <AnalysisTabSkeleton
        gridClassName='grid-cols-[1fr_1.2fr_0.8fr]'
        columns={3}
      />
    );
  }

  if (!rows.length) {
    return (
      <AnalysisTabEmptyState message='Sem vidas com data de nascimento para o cálculo de idade/capital.' />
    );
  }

  return (
    <div className='overflow-x-auto'>
      <div className='min-w-[480px]'>
        <div className='grid grid-cols-[1fr_1.2fr_0.8fr] gap-4 pb-4'>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Idade
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Capital
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Vidas
          </Text>
        </div>

        {rows.map((row, index) => (
          <div
            key={index}
            className='grid grid-cols-[1fr_1.2fr_0.8fr] gap-4 py-2.5'
          >
            <Text className='font-montserrat text-sm text-alba-black-500'>
              {row.age}
            </Text>
            <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
              {row.capital}
            </Text>
            <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
              {row.lives}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Aba "Afastados" ────────────────────────────────────────────────────────

export function PmeAfastadosTab({
  rows,
  isLoading = false,
}: {
  rows: PmeAnalysisAfastadoRow[];
  isLoading?: boolean;
}) {
  const { data: cidDescriptions } = useCidDescriptions(
    rows.flatMap(row => row.cids)
  );
  if (isLoading) {
    return (
      <AnalysisTabSkeleton
        gridClassName='grid-cols-[2fr_1fr_0.7fr_1.2fr_1.6fr]'
        columns={5}
      />
    );
  }

  if (!rows.length) {
    return (
      <AnalysisTabEmptyState message='Nenhuma vida afastada nesta proposta.' />
    );
  }

  return (
    <div className='overflow-x-auto'>
      <div className='min-w-[640px]'>
        <div className='grid grid-cols-[2fr_1fr_0.7fr_1.2fr_1.6fr] gap-4 pb-4'>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Nome
          </Text>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Sexo
          </Text>
          <Text className='font-montserrat text-xs font-bold text-alba-black-500'>
            Idade
          </Text>
          <Text className='text-right font-montserrat text-xs font-bold text-alba-black-500'>
            Capital
          </Text>
          <Text className='text-left font-montserrat text-xs font-bold text-alba-black-500'>
            CID
          </Text>
        </div>

        {rows.map((row, index) => (
          <div
            key={index}
            className='grid grid-cols-[2fr_1fr_0.7fr_1.2fr_1.6fr] gap-4 py-3'
          >
            <Text className='font-montserrat text-sm text-alba-black-500'>
              {row.name}
            </Text>
            <Text className='font-montserrat text-sm text-alba-gray-600'>
              {row.sex}
            </Text>
            <Text className='font-montserrat text-sm text-alba-gray-600'>
              {row.age}
            </Text>
            <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
              {row.capital}
            </Text>
            <Text className='text-right font-montserrat text-sm text-alba-gray-600'>
              <AnalysisCidList codes={row.cids} descriptions={cidDescriptions} />
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
}
